import React from 'react';
import LineChart from '@cloudscape-design/components/line-chart';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import SegmentedControl from '@cloudscape-design/components/segmented-control';

import { ResultRow } from '../api/ForecastApi';
import { DivergencePoint } from '../utils/divergence';

interface DivergenceChartProps {
  rows: ResultRow[];
  divergencePoints: DivergencePoint[];
  topDivergentTargetDates: string[];
  requestedTargetDates?: string[];
  /** Called when the user clicks 'Investigate this FCD' in a metric point popover. */
  onInvestigate?: (fcd: string) => void;
}

type Quantile = 'p50' | 'p90';
type QuantileSel = Quantile | 'both';
type Adjustment = 'raw' | 'post_causal';
type Scope = 'inner' | 'outer';

const qName = (q: Quantile) => (q === 'p50' ? 'P50' : 'P90');

const fmt2 = (v: number): string =>
  Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v);

export default function DivergenceChart({
  rows,
  divergencePoints,
  onInvestigate,
}: DivergenceChartProps): JSX.Element {
  // Toggles keep each chart readable. Quantile (P50 / P90 / Both) is shared by both panels;
  // the levels panel also toggles the causal adjustment (Raw / Post-causal) and the metric
  // panel the cell scope (Inner / Outer). "Both" shows P50 and P90 together as a band.
  const [quantileSel, setQuantileSel] = React.useState<QuantileSel>('both');
  const [adjustment, setAdjustment] = React.useState<Adjustment>('raw');
  const [scope, setScope] = React.useState<Scope>('inner');

  if (rows.length === 0) {
    return <Box textAlign="center">No divergence data available.</Box>;
  }

  if (rows.length < 2) {
    return (
      <Alert type="info">
        Insufficient data for divergence comparison. At least 2 forecast runs are needed.
      </Alert>
    );
  }

  // Sort rows by fcd ascending — shared x-axis for both stacked panels.
  const sortedRows = [...rows].sort((a, b) =>
    String(a['fcd'] ?? '').localeCompare(String(b['fcd'] ?? ''))
  );
  // x-axis is the set of distinct FCDs (multiple FGs share the same FCDs).
  const xDomain = Array.from(new Set(sortedRows.map((r) => String(r['fcd'] ?? ''))));

  // Whether the auditor chi-squared metric columns are present in the results.
  const hasMetric = sortedRows.some(
    (r) => r['p50_inner_metric'] != null || r['p50_outer_metric'] != null
  );

  // Distinct FGs in the result. With more than one FG we draw a separate line per FG,
  // otherwise a single line with a clean title.
  const fgValues = Array.from(
    new Set(sortedRows.map((r) => String(r['fg'] ?? '')).filter((v) => v !== ''))
  ).sort();
  const multiFg = fgValues.length > 1;

  // Which quantile(s) to plot given the toggle.
  const quantiles: Quantile[] = quantileSel === 'both' ? ['p50', 'p90'] : [quantileSel];
  const qTitle = quantileSel === 'both' ? 'P50 & P90' : qName(quantileSel);
  const aLabel = adjustment === 'raw' ? 'Raw' : 'Post-causal';

  // Series title / legend / filter label carries only the dimensions that actually vary within
  // a chart. The adjustment (levels) and scope (metric) are fixed per chart and shown in the
  // header, so they're omitted. The quantile is included only when "Both" is selected (otherwise
  // it's constant too); the FGID is included only when more than one FG is plotted. With a single
  // quantile and a single FG we fall back to the quantile name so the legend isn't blank.
  const seriesTitle = (q: Quantile, fg?: string): string => {
    const parts: string[] = [];
    if (quantileSel === 'both') parts.push(qName(q));
    if (fg != null) parts.push(fg);
    return parts.length > 0 ? parts.join(' · ') : qName(q);
  };

  // Build one line series per FG (or a single series when there is only one FG).
  // Colors are left unset so Cloudscape assigns a distinct color per series — hardcoding
  // would make different FGs share a color and become indistinguishable.
  const seriesForQuantile = (key: string, q: Quantile) => {
    const build = (src: ResultRow[], title: string) => ({
      title,
      type: 'line' as const,
      valueFormatter: fmt2,
      // Metric is null for the first FCD (no previous run); drop null points.
      data: src
        .filter((r) => r[key] != null && r[key] !== '')
        .map((r) => ({ x: String(r['fcd'] ?? ''), y: Number(r[key]) })),
    });
    if (!multiFg) {
      return [build(sortedRows, seriesTitle(q))];
    }
    return fgValues.map((fg) =>
      build(sortedRows.filter((r) => String(r['fg'] ?? '') === fg), seriesTitle(q, fg))
    );
  };

  // NOTE: the RR auditor's audit_threshold / audit_inner_threshold are intentionally
  // NOT drawn here. Those cutoffs apply to a BUNDLE-LEVEL, cumulative chi-squared
  // statistic the auditor computes across all FGs (running_metric over accumulated
  // diff/size in RRMultiFGForecastBundleAuditor). They are on a different scale from
  // this per-(fg,fcd) metric, so a horizontal reference line here would be misleading.

  // --- Levels panel (top): forecast level for the selected quantile(s) + adjustment ---
  const levelSeries = quantiles
    .flatMap((q) => seriesForQuantile(`${q}_${adjustment}`, q))
    .filter((s) => s.data.length > 0);

  // --- Metric panel (bottom): chi-squared for the selected quantile(s) + scope ---
  const metricLines = quantiles
    .flatMap((q) => seriesForQuantile(`${q}_${scope}_metric`, q))
    .filter((s) => s.data.length > 0);
  const metricSeries = metricLines;

  // Scale the metric chart to the plotted line values for the active toggle selection.
  const metricYs = metricLines.flatMap((s) => s.data.map((d) => d.y));
  let metricYDomain: [number, number] | undefined;
  if (metricYs.length > 0) {
    const lo = Math.min(...metricYs);
    const hi = Math.max(...metricYs);
    const pad = (hi - lo) * 0.1 || Math.abs(hi) * 0.1 || 1;
    metricYDomain = [Math.min(0, lo - pad), hi + pad];
  }

  // A fresh element per call — reusing one element instance across both panels makes the two
  // segmented controls share internal radio-group identity and desync on state changes.
  const renderQuantileToggle = () => (
    <SegmentedControl
      selectedId={quantileSel}
      onChange={({ detail }) => setQuantileSel(detail.selectedId as QuantileSel)}
      label="Quantile"
      options={[
        { text: 'P50', id: 'p50' },
        { text: 'P90', id: 'p90' },
        { text: 'Both', id: 'both' },
      ]}
    />
  );

  return (
    <SpaceBetween size="l">
      {/* Top: forecast-over-forecast levels for the chosen quantile(s) + adjustment */}
      <SpaceBetween size="xs">
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {renderQuantileToggle()}
              <SegmentedControl
                selectedId={adjustment}
                onChange={({ detail }) => setAdjustment(detail.selectedId as Adjustment)}
                label="Adjustment"
                options={[
                  { text: 'Raw', id: 'raw' },
                  { text: 'Post-causal', id: 'post_causal' },
                ]}
              />
            </SpaceBetween>
          }
          description="Total predicted demand for the selected FG(s) at each forecast creation date (FCD), summed across the horizon. Comparing consecutive runs shows how the forecast moves over time (forecast-over-forecast). Toggle quantile (P50 / P90 / Both — Both shows the two as a band) and adjustment (Raw = before causal adjustments, Post-causal = after)."
        >
          Forecast levels ({qTitle}) — {aLabel}
        </Header>
        <LineChart
          key={`levels-${quantileSel}-${adjustment}`}
          series={levelSeries as any}
          xDomain={xDomain}
          xScaleType="categorical"
          xTitle="Forecast Creation Date (FCD)"
          yTitle="Predicted Demand"
          yTickFormatter={fmt2}
          height={300}
          empty={<Box textAlign="center">No data</Box>}
          noMatch={<Box textAlign="center">No visible series</Box>}
        />
        {divergencePoints.length > 0 && (
          <Box variant="small" color="text-status-info">
            {divergencePoints.length} divergence point(s) detected (threshold: 20% relative change)
          </Box>
        )}
      </SpaceBetween>

      {/* Bottom: auditor chi-squared metric for the chosen quantile(s) + scope */}
      {hasMetric && (
        <SpaceBetween size="xs">
          <Header
            variant="h3"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {renderQuantileToggle()}
                <SegmentedControl
                  selectedId={scope}
                  onChange={({ detail }) => setScope(detail.selectedId as Scope)}
                  label="Scope"
                  options={[
                    { text: 'Inner', id: 'inner' },
                    { text: 'Outer', id: 'outer' },
                  ]}
                />
              </SpaceBetween>
            }
            description="How much each run changed vs the immediately preceding run, scored with the RR forecast auditor's chi-squared statistic ((Σ(curr−prev)²/(curr+prev+ε) − N)/√N) over (FG, target date) cells summed across ASINs. Higher = a bigger run-over-run shift. Computed on the raw forecast. Inner = cells present in both runs; Outer = union of cells (a missing side counts as 0). The quantile toggle is shared with the panel above."
          >
            Auditor χ² divergence metric ({qTitle}) — {scope === 'inner' ? 'Inner' : 'Outer'}
          </Header>
          {metricLines.length > 0 ? (
            <>
              <LineChart
                key={`metric-${quantileSel}-${scope}`}
                series={metricSeries as any}
                xDomain={xDomain}
                yDomain={metricYDomain}
                xScaleType="categorical"
                xTitle="Forecast Creation Date (FCD)"
                yTitle="Auditor metric (χ²)"
                yTickFormatter={fmt2}
                height={300}
                detailPopoverFooter={onInvestigate ? (fcd: string) => (
                  <Button iconName="external" onClick={() => onInvestigate(String(fcd))}>
                    Investigate this FCD
                  </Button>
                ) : undefined}
                empty={<Box textAlign="center">No data</Box>}
                noMatch={<Box textAlign="center">No visible series</Box>}
              />
              <Box variant="small" color="text-status-info">
                {scope === 'inner' ? (
                  <>
                    <strong>Inner</strong>: χ² over cells present in both the current and previous
                    run.
                  </>
                ) : (
                  <>
                    <strong>Outer</strong>: χ² over the union of cells across both runs (a cell
                    missing on one side counts as 0), so it also captures cells that appeared or
                    dropped out.
                  </>
                )}{' '}
                The metric here is relative to the previous run in the selected window, so
                magnitudes can differ from a specific pipeline audit when approvals were skipped.
              </Box>
            </>
          ) : (
            <Alert type="info">
              Not enough consecutive runs to compute the auditor metric (need at least 2 FCDs).
            </Alert>
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
